<template>
  <div class="pc-note-preview__content typo" v-html="html" ref="preview">
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import MarkdownIt from 'markdown-it'
import hljs from 'highlight.js/lib/core'
import 'highlight.js/styles/tomorrow-night-bright.css'
import copy from 'copy-text-to-clipboard'
import _ from 'lodash'
import eventBus from '@/eventbus'

import clike from 'highlight.js/lib/languages/c-like'
import cpp from 'highlight.js/lib/languages/cpp'
import xml from 'highlight.js/lib/languages/xml'
import c from 'highlight.js/lib/languages/c'
import csharp from 'highlight.js/lib/languages/csharp'
import css from 'highlight.js/lib/languages/css'
import markdown from 'highlight.js/lib/languages/markdown'
import dockerfile from 'highlight.js/lib/languages/dockerfile'
import go from 'highlight.js/lib/languages/go'
import gradle from 'highlight.js/lib/languages/gradle'
import groovy from 'highlight.js/lib/languages/groovy'
import http from 'highlight.js/lib/languages/http'
import lua from 'highlight.js/lib/languages/lua'
import perl from 'highlight.js/lib/languages/perl'
import nginx from 'highlight.js/lib/languages/nginx'
import php from 'highlight.js/lib/languages/php'
import powershell from 'highlight.js/lib/languages/powershell'
import python from 'highlight.js/lib/languages/python'
import scala from 'highlight.js/lib/languages/scala'
import scss from 'highlight.js/lib/languages/scss'
import shell from 'highlight.js/lib/languages/shell'
import sql from 'highlight.js/lib/languages/sql'
import yaml from 'highlight.js/lib/languages/yaml'
import typescript from 'highlight.js/lib/languages/typescript'
import swift from 'highlight.js/lib/languages/swift'
import makefile from 'highlight.js/lib/languages/makefile'
import java from 'highlight.js/lib/languages/java'
import json from 'highlight.js/lib/languages/json'
import kotlin from 'highlight.js/lib/languages/kotlin'
import less from 'highlight.js/lib/languages/less'
import javascript from 'highlight.js/lib/languages/javascript'

hljs.registerLanguage('c-like', clike)
hljs.registerLanguage('cpp', cpp)
hljs.registerLanguage('xml', xml)
hljs.registerLanguage('c', c)
hljs.registerLanguage('csharp', csharp)
hljs.registerLanguage('css', css)
hljs.registerLanguage('markdown', markdown)
hljs.registerLanguage('dockerfile', dockerfile)
hljs.registerLanguage('go', go)
hljs.registerLanguage('gradle', gradle)
hljs.registerLanguage('groovy', groovy)
hljs.registerLanguage('http', http)
hljs.registerLanguage('java', java)
hljs.registerLanguage('javascript', javascript)
hljs.registerLanguage('json', json)
hljs.registerLanguage('kotlin', kotlin)
hljs.registerLanguage('less', less)
hljs.registerLanguage('lua', lua)
hljs.registerLanguage('makefile', makefile)
hljs.registerLanguage('perl', perl)
hljs.registerLanguage('nginx', nginx)
hljs.registerLanguage('php', php)
hljs.registerLanguage('powershell', powershell)
hljs.registerLanguage('python', python)
hljs.registerLanguage('scala', scala)
hljs.registerLanguage('scss', scss)
hljs.registerLanguage('shell', shell)
hljs.registerLanguage('sql', sql)
hljs.registerLanguage('swift', swift)
hljs.registerLanguage('yaml', yaml)
hljs.registerLanguage('typescript', typescript)

export default {
  data () {
    return {
      html: '',
      codes: {},
      scrollEffectDebounce: _.debounce(this.scrollEffect, 500)
    }
  },
  mounted () {
    this.$refs.preview.addEventListener('click', this.copyCodeBlockHandler)
    this.$refs.preview.addEventListener('dblclick', this.copyCodeHandler)
    this.$refs.preview.addEventListener('click', this.taggleFoldHandler)
    window.document.getElementById('pc-page__note').addEventListener('scroll', this.scrollHandler)
    eventBus.$on('WORKBENCH_PREVIEW_SCROLL_LINE_INTOVIEW', this.scrollLineIntoView)
    eventBus.$on('WORKBENCH_PREVIEW_UPDATE_HTML', this.updateHtml)
  },
  beforeDestroy () {
    if (this.$refs.preview) {
      this.$refs.preview.removeEventListener('click', this.copyCodeBlockHandler)
      this.$refs.preview.removeEventListener('dblclick', this.copyCodeHandler)
      this.$refs.preview.removeEventListener('click', this.taggleFoldHandler)
    }
    const pageNoteElement = window.document.getElementById('pc-page__note')
    if (pageNoteElement) {
      pageNoteElement.removeEventListener('scroll', this.scrollHandler)
    }
    eventBus.$off('WORKBENCH_PREVIEW_SCROLL_LINE_INTOVIEW', this.scrollLineIntoView)
    eventBus.$off('WORKBENCH_PREVIEW_UPDATE_HTML', this.updateHtml)
  },
  computed: {
    ...mapGetters('workbench/current', {
      preview: 'preview',
      note: 'note',
      syncScrollCodePosition: 'syncScrollCodePosition',
      syncScrollItems: 'syncScrollItems'
    }),
    ...mapGetters('workbench/outline', {
      foldedHeaderLineNumbersOriginal: 'foldedHeaderLineNumbers',
      headers: 'headers'
    }),
    content () {
      return this.note.content
    },
    foldedHeaderLineNumbers () {
      return _.cloneDeep(this.foldedHeaderLineNumbersOriginal)
    }
  },
  watch: {
    preview (newValue, oldValue) {
      if (newValue) {
        this.updateHtml()
        this.$nextTick(() => {
          this.fold(this.foldedHeaderLineNumbers)
          this.syncScrollTop()
          this.updateSyncScrollPreviewPosition()
        })
      }
    },
    // https://stackoverflow.com/questions/50682261/vue-watch-array-push-same-old-and-new-values
    foldedHeaderLineNumbers (newValue, oldValue) {
      if (this.preview) {
        const addFoldedHeaderLineNumbers = _.difference(newValue, oldValue)
        const removeFoldedHeaderLineNumbers = _.difference(oldValue, newValue)
        this.fold(addFoldedHeaderLineNumbers)
        this.unfold(removeFoldedHeaderLineNumbers)
      }
    }
  },
  methods: {
    ...mapMutations('workbench/current', {
      setSyncScrollPreviewPosition: 'SET_SYNC_SCROLL_PREVIEW_POSITION'
    }),
    ...mapMutations('workbench/outline', {
      setOutlineActiveHeaderLineNumber: 'SET_ACTIVE_HEADER_LINENUMBER'
    }),
    isFolded (lineNumber) {
      return _.indexOf(this.foldedHeaderLineNumbers, lineNumber) > -1
    },
    updateHtml () {
      this.codes = {}
      const md = new MarkdownIt({
        html: true,
        linkify: true
      })

      // 代码块
      const that = this
      function rendererRulesFence (tokens, idx, options, env, slf) {
        const token = tokens[idx]
        const code = token.content
        const language = token.info
        const html = hljs.highlightAuto(code, [language]).value
        const id = Math.floor(Math.random() * 100000000).toString()
        that.codes[id] = code
        return `
          <pre class="line" data-line="${token.map[0]}">
            <code class="language-${language} hljs">${html}</code>
            <span class="pre-tools">
              <span>${language}</span>
              <span id="${id}" class="copy-code btn" title="复制代码"><i class="el-icon-document-copy"></i>复制代码</span>
            </span>
          </pre>
        `
      }
      md.renderer.rules.fence = rendererRulesFence

      // 注入行号
      function injectLineNumbers (tokens, idx, options, env, slf) {
        if (tokens[idx].map) {
          tokens[idx].attrJoin('class', 'line')
          tokens[idx].attrSet('data-line', String(tokens[idx].map[0]))
          // tokens[idx].attrSet('data-line-end', String(tokens[idx].map[1]))
        }
        return slf.renderToken(tokens, idx, options, env, slf)
      }

      // header
      function rendererRulesHeading (tokens, idx, options, env, slf) {
        const token = tokens[idx]
        const lineNumber = token.map[0]
        const tag = token.tag
        const foldedClass = that.isFolded(lineNumber) ? 'folded' : 'unfolded'
        return `<${tag} class="line ${foldedClass}" data-line="${lineNumber}"><span class="pc-heading-gutter"><i class="el-icon-arrow-down"></i><i class="el-icon-arrow-right"></i></span>`
        // if (tokens[idx].map) {
        //   tokens[idx].attrJoin('class', 'line')
        //   tokens[idx].attrSet('data-line', String(tokens[idx].map[0]))
        //   // tokens[idx].attrSet('data-line-end', String(tokens[idx].map[1]))
        // }
      }
      function rendererRulesHeadingClose (tokens, idx, options, env, slf) {
        const token = tokens[idx]
        const tag = token.tag
        return `<span class="pc-heading-more"><i class="el-icon-more"></i></span></${tag}>`
      }

      md.renderer.rules.paragraph_open = injectLineNumbers
      // md.renderer.rules.heading_open = injectLineNumbers
      md.renderer.rules.heading_open = rendererRulesHeading
      md.renderer.rules.heading_close = rendererRulesHeadingClose
      md.renderer.rules.list_item_open = injectLineNumbers
      md.renderer.rules.table_open = injectLineNumbers

      // code_inline
      // const defaultRendererRulesCodeInline = md.renderer.rules.code_inline
      // md.renderer.rules.code_inline = (tokens, idx, options, env, slf) => {
      //   tokens[idx].attrJoin('class', 'codespan')
      //   return defaultRendererRulesCodeInline(tokens, idx, options, env, slf)
      // }
      md.renderer.rules.code_inline = (tokens, idx, options, env, slf) => {
        const token = tokens[idx]
        const code = token.content
        return `<code class="codespan">${md.utils.escapeHtml(code).replaceAll(' ', '&nbsp;')}</code>`
      }

      // link
      md.renderer.rules.link_open = (tokens, idx, options, env, slf) => {
        if (tokens[idx].attrGet('href') && tokens[idx].attrGet('href').indexOf('//') > -1) {
          tokens[idx].attrSet('target', '_blank')
        }
        return slf.renderToken(tokens, idx, options, env, slf)
      }

      this.html = md.render(this.content)
      window.markdownTokens = md.parse(this.content)
    },
    copyCodeBlockHandler (event) {
      const target = event.target
      if (target.className === 'copy-code btn') {
        copy(this.codes[target.id])
      }
    },
    copyCodeHandler (event) {
      const target = event.target
      if (target.className === 'codespan') {
        copy(target.textContent)
      }
    },
    taggleFoldHandler () {
      const target = event.target
      if (target.tagName === 'SPAN' && target.className.indexOf('pc-heading-gutter') > -1) {
        const lineNumber = +(target.parentElement.getAttribute('data-line'))
        eventBus.$emit('WORKBENCH_CODEMIRROR_EDITOR_TOGGLE_FOLDED', lineNumber)
      }
    },
    getElHeight (el) {
      const elMarginBottom = +(window.getComputedStyle(el).marginBottom.replace('px', ''))
      const boundingClientRect = el.getBoundingClientRect()
      return boundingClientRect.height + elMarginBottom
    },
    syncScrollTop () {
      const syncScrollItem = this.syncScrollCodePosition.item
      const containerEl = window.document.querySelector('#pc-page__note')
      if (syncScrollItem === null) {
        containerEl.scrollTo(0, 0)
      } else {
        const syncScrollEl = window.document.querySelector(`.line[data-line="${syncScrollItem.startLine}"]`)
        const contailerScrollTop = containerEl.scrollTop
        const syncScrollElDefaultTop = contailerScrollTop + syncScrollEl.getBoundingClientRect().top
        const syncScrollElHeight = this.getElHeight(syncScrollEl)
        const newScrollTop = syncScrollElDefaultTop + syncScrollElHeight * this.syncScrollCodePosition.percent
        containerEl.scrollTo(0, newScrollTop)
      }
    },
    updateSyncScrollPreviewPosition () {
      let syncScrollElHeight
      let top
      let currentItem = null
      for (let i = 0; i < this.syncScrollItems.length; i++) {
        const syncScrollItem = this.syncScrollItems[i]
        const syncScrollEl = window.document.querySelector(`.line[data-line="${syncScrollItem.startLine}"]`)
        const elMarginBottom = +(window.getComputedStyle(syncScrollEl).marginBottom.replace('px', ''))
        const boundingClientRect = syncScrollEl.getBoundingClientRect()
        top = boundingClientRect.top
        const bottom = boundingClientRect.bottom + elMarginBottom
        if (top < 0 && bottom > 0) {
          syncScrollElHeight = boundingClientRect.height + elMarginBottom
          currentItem = syncScrollItem
          break
        }
      }

      let previewPosition
      if (currentItem === null) {
        previewPosition = {
          item: null,
          percent: 0
        }
      } else {
        top = top >= 0 ? 0 : -top
        previewPosition = {
          item: currentItem,
          percent: top / syncScrollElHeight
        }
      }

      this.setSyncScrollPreviewPosition(previewPosition)
    },
    updateActiveHeader () {
      let activeHeaderLineNumber = this.headers[0].lineNumber
      for (let i = 1; i < this.headers.length; i++) {
        const header = this.headers[i]
        const headerEl = window.document.querySelector(`.line[data-line="${header.lineNumber}"]`)
        const rect = headerEl.getBoundingClientRect()
        if (rect.height === 0) {
          continue
        }
        if (rect.top <= 50) {
          activeHeaderLineNumber = header.lineNumber
        } else {
          break
        }
      }
      this.setOutlineActiveHeaderLineNumber(activeHeaderLineNumber)
    },
    scrollEffect () {
      this.updateActiveHeader()
      this.updateSyncScrollPreviewPosition()
    },
    scrollHandler () {
      if (this.preview) {
        this.scrollEffectDebounce()
      }
    },
    fold (headerLineNumbers) {
      headerLineNumbers.forEach(headerLineNumber => {
        const headerInfo = _.find(this.headers, n => n.lineNumber === headerLineNumber)
        const tag = `H${headerInfo.level}`
        const hiddenClassName = `${tag}-hidden`
        const headerEl = window.document.querySelector(`#pc-page__note h${headerInfo.level}.line[data-line="${headerLineNumber}"]`)
        headerEl.className = 'line folded'
        const headerSiblings = window.document.querySelectorAll(`#pc-page__note h${headerInfo.level}.line[data-line="${headerLineNumber}"] ~ *`)
        for (let i = 0; i < headerSiblings.length; i++) {
          const el = headerSiblings[i]
          if (el.tagName.indexOf('H') === 0) {
            const elLevel = +(el.tagName.replace('H', ''))
            if (headerInfo.level >= elLevel) {
              return
            }
          }
          if (el.className && el.className.indexOf(hiddenClassName) === -1) {
            el.className = el.className + ` ${hiddenClassName}`
          } else {
            el.className = hiddenClassName
          }
        }
      })
    },
    unfold (headerLineNumbers) {
      headerLineNumbers.forEach(headerLineNumber => {
        const headerInfo = _.find(this.headers, n => n.lineNumber === headerLineNumber)
        const tag = `H${headerInfo.level}`
        const hiddenClassName = `${tag}-hidden`
        const headerEl = window.document.querySelector(`#pc-page__note h${headerInfo.level}.line[data-line="${headerLineNumber}"]`)
        headerEl.className = 'line unfolded'
        const headerSiblings = window.document.querySelectorAll(`#pc-page__note h${headerInfo.level}.line[data-line="${headerLineNumber}"] ~ *`)
        for (let i = 0; i < headerSiblings.length; i++) {
          const el = headerSiblings[i]
          if (el.tagName.indexOf('H') === 0) {
            const elLevel = +(el.tagName.replace('H', ''))
            if (headerInfo.level >= elLevel) {
              return
            }
          }
          if (el.className) {
            const regex = new RegExp(`\\s*${hiddenClassName}|^\\s*`, 'gi')
            el.className = el.className.replace(regex, '').replace(regex, '')
          }
        }
      })
    },
    scrollLineIntoView (lineNumber) {
      window.document.querySelector(`.line[data-line="${lineNumber}"]`).scrollIntoView()
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/variables.scss';
.pc-note-preview__content {
  min-height: 200px;
  margin: 1rem 2rem 5rem 2rem;
  font-size: 1.4rem;

  h1,h2,h3,h4,h5,h6 {
    position: relative;

    // &:first-child {
    //   margin-top: 0;
    // }
  }

  .H1-hidden,.H2-hidden,.H3-hidden,.H4-hidden,.H5-hidden,.H6-hidden {
    display: none;
  }

  .pc-heading-more {
    color: #999;
    font-size: 1.6rem;
    vertical-align: middle;
    margin-left: 5px;
    display: none;
  }

  .folded .pc-heading-more {
    display: inline;
  }
}

.pc-heading-gutter {
  position: absolute;
  left: -2rem;
  width: 2rem;
  color: #999;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  height: 100%;

  /* https://stackoverflow.com/questions/9183381/how-to-have-click-event-only-fire-on-parent-div-not-children */
  i {
    pointer-events: none;
  }
}

.folded .pc-heading-gutter i.el-icon-arrow-down{
  display: none;
}

.unfolded .pc-heading-gutter i.el-icon-arrow-right {
  display: none;
}

// .pc-heading-folded::after {
//   content: "\25BE";
// }
</style>
